import { BREAKPOINTS } from "../config/breakpoints";
import React, { useState } from "react";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import Button from "../Generic/Button";
import InputBox from "../Generic/InputBox";
import {
	errorNotification,
	successNotification,
} from "../Generic/Notifications";
import { forceDownloadCSV } from "../util/util";
import BusinessService from "./Services/BusinessService";

const Page = styled.div`
	height: 100%;
	width: 100%;
	display: flex;
	flex-direction: column;
	padding: 0 2rem 2rem 2rem;
	@media (max-width: ${BREAKPOINTS.desktop}px) {
		padding: 0 0 2rem 0;
	}
`;

const Title = styled.h1`
	font-size: 1.5rem;
	font-weight: 600;
	margin: 1rem 0 1.5rem 0;
`;

const Row = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: flex-end;
	gap: 1rem;
	margin-bottom: 1rem;
`;

const Field = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.35rem;
`;

const Label = styled.span`
	font-size: 0.95rem;
	font-weight: 500;
`;

/** Backend expects `dd/MM/yyyy` (see dashboard `generateBalanceSheetForGivenPeriod`). */
function toDdMmYyyy(yyyyMmDd) {
	if (!yyyyMmDd) return "";
	const [y, m, d] = yyyyMmDd.split("-");
	if (!y || !m || !d) return "";
	return `${d}/${m}/${y}`;
}

const BalanceSheet = () => {
	const { machineId: restaurantId } = useParams();
	const [startDate, setStartDate] = useState("");
	const [endDate, setEndDate] = useState("");
	const [loading, setLoading] = useState(false);

	async function handleGenerateCsv() {
		if (!startDate || !endDate) {
			errorNotification("Please select both start and end dates.");
			return;
		}
		if (startDate > endDate) {
			errorNotification("Start date must be before or equal to end date.");
			return;
		}

		const startDateParam = toDdMmYyyy(startDate);
		const endDateParam = toDdMmYyyy(endDate);

		setLoading(true);
		const { data, error } = await BusinessService.getBalanceSheetCsv(
			restaurantId,
			{ startDate: startDateParam, endDate: endDateParam }
		);

		if (error) {
			errorNotification(
				typeof error === "string" ? error : "Could not download balance sheet CSV."
			);
			setLoading(false);
			return;
		}
		if (data == null || data === "") {
			errorNotification("Empty CSV received from the server.");
			setLoading(false);
			return;
		}

		const fileName = `balance-sheet-${restaurantId}_${startDate}_${endDate}.csv`;
		forceDownloadCSV(data, fileName);
		successNotification("Balance sheet CSV downloaded.");
		setLoading(false);
	}

	return (
		<Page>
			<Title>Balance Sheet</Title>
			<Row>
				<Field>
					<Label>Start date</Label>
					<InputBox
						type="date"
						padding="0.5rem"
						width="12rem"
						value={startDate}
						onChange={(e) => setStartDate(e.target.value)}
						disabled={loading}
					/>
				</Field>
				<Field>
					<Label>End date</Label>
					<InputBox
						type="date"
						padding="0.5rem"
						width="12rem"
						value={endDate}
						onChange={(e) => setEndDate(e.target.value)}
						disabled={loading}
					/>
				</Field>
				<Button onClick={handleGenerateCsv} disabled={loading || (!startDate || !endDate)}>
					{loading ? "Generating…" : "Generate CSV"}
				</Button>
			</Row>
		</Page>
	);
};

export default BalanceSheet;
